import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Permission } from '@consumer/app/config/permission.config'
import { ItemContentService } from '@consumer/app/services/item/item-content.service'
import { SellerItemService } from '@consumer/app/services/seller-item.service'
import { PermissionStore } from '@engineering11/access-web'
import { deepCopy, isNotNil } from '@engineering11/utility'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Action, Store } from '@ngrx/store'
import { combineLatestWith } from 'rxjs'
import { filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators'
import { getCurrentUser, NotificationTranslateService, SELLER_ROUTES, SellerNavigationService } from 'shared-lib'
import {
  DeleteItem,
  DeleteItemSuccess,
  DelistItem,
  DelistItemSuccess,
  GetSellerItems,
  GetSellerItemsSuccess,
  NoSellerAction,
  OnInitSellerItems,
  PublishItem,
  PublishItemSuccess,
  SelectItem,
  SelectItemById,
  SellerItemActionTypes,
  UpdateItem,
  UpdateItemSuccess,
} from './seller-item.actions'
import { sellerItemsLoaded } from './seller-item.selectors'

@Injectable()
export class SellerItemEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private permissionStore: PermissionStore,
    private itemContentService: ItemContentService,
    private sellerItemService: SellerItemService,
    private notificationService: NotificationTranslateService,
    private sellerNavigationService: SellerNavigationService,
    private router: Router
  ) {}

  ngrxOnInitEffects(): Action {
    return { type: SellerItemActionTypes.onInitSellerItems }
  }

  onInit$ = createEffect(() => {
    return this.actions$.pipe(
      ofType<OnInitSellerItems>(SellerItemActionTypes.onInitSellerItems),
      switchMap(_ => this.store.select(getCurrentUser)),
      filter(isNotNil),
      withLatestFrom(this.permissionStore.hasPermission(Permission.SellItems).pipe(filter(isNotNil))),
      map(([user, isSeller]) => (isSeller ? new GetSellerItems(user) : new NoSellerAction()))
    )
  })

  $onGetSellerItems = createEffect(() =>
    this.actions$.pipe(
      ofType<GetSellerItems>(SellerItemActionTypes.getSellerItems),
      switchMap(action => {
        return this.sellerItemService.getAllProductItems(action.payload.customerKey)
      }),
      map(result => new GetSellerItemsSuccess(result))
    )
  )

  $onUpdateSellerItem = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateItem>(SellerItemActionTypes.updateItem),
      switchMap(action => this.itemContentService.persist(deepCopy(action.payload as any))), // TODO: Fix typing
      map(result => new UpdateItemSuccess(result))
    )
  )

  $onUpdateSellerItemSuccess = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateItem>(SellerItemActionTypes.updateItemSuccess),
      map(result => {
        // Routing to the new item if created.
        // This currently scrolls the user to the top on item creation
        // this is a known issue that we intend to fix on a later iteration
        if (this.router.url.endsWith(SELLER_ROUTES.VIEW_EDIT_ITEMS)) {
          this.sellerNavigationService.toItemDetail(result.payload.contentId, {
            replaceUrl: true,
          })
        }
        return new NoSellerAction()
      })
    )
  )

  $onPublishItem = createEffect(() =>
    this.actions$.pipe(
      ofType<PublishItem>(SellerItemActionTypes.publishItem),
      withLatestFrom(this.store.select(getCurrentUser).pipe(filter(isNotNil))),
      switchMap(async ([action, user]) => {
        return this.itemContentService.updateAndPublish(action.payload, user)
      }),
      tap(_ => {
        this.notificationService.popNotification({
          title: 'Item Published',
          message: 'You have published this as an active listing!',
          type: 'success',
          autoClose: true,
        })
      }),
      map(result => new PublishItemSuccess(result))
    )
  )

  $onDelistItem = createEffect(() =>
    this.actions$.pipe(
      ofType<DelistItem>(SellerItemActionTypes.delistItem),
      withLatestFrom(this.store.select(getCurrentUser).pipe(filter(isNotNil))),
      switchMap(async ([action, user]) => {
        return this.itemContentService.delistItem(action.payload, user)
      }),
      map(result => new DelistItemSuccess(result))
    )
  )

  $onDeleteItem = createEffect(() =>
    this.actions$.pipe(
      ofType<DeleteItem>(SellerItemActionTypes.deleteItem),
      withLatestFrom(this.store.select(getCurrentUser).pipe(filter(isNotNil))),
      switchMap(async ([action, user]) => {
        await this.itemContentService.summary.archiveWithId(action.payload.contentId)
        return action.payload
      }),
      map(result => new DeleteItemSuccess(result))
    )
  )

  $onSelectItemById = createEffect(() =>
    this.actions$.pipe(
      ofType<SelectItemById>(SellerItemActionTypes.selectItemById),
      combineLatestWith(this.store.select(sellerItemsLoaded)),
      filter(([action, loaded]) => loaded),
      map(([itemId]) => new SelectItem(itemId.payload))
    )
  )
}
